import React, { useState } from 'react';
import SafeIcon from '../../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useFinancial } from '../../../context/FinancialContext';
import QuickAddForm from './QuickAddForm';
import { fmt, FREQ_ABBR, isDiscretionary, sumMonthly, futureValue } from './walkthroughUtils';

const { FiTrash2, FiTrendingUp } = FiIcons;

const HOW = [
  ['Pull your statements', 'Gather three months of bank and credit-card activity.'],
  ['Separate needs from wants', 'Mark required bills and discretionary purchases.'],
  ['Enter your totals', 'Use 3-Mo Total to turn three months of data into a monthly average.']
];

const HowToGather = () => (
  <div className="bg-white rounded-2xl border border-slate-100 p-4 shadow-sm">
    <p className="text-[9px] font-black uppercase text-slate-400 tracking-widest mb-3">Your three-month check</p>
    <ol className="relative space-y-4">
      <span className="absolute left-3 top-3 bottom-3 w-px bg-slate-100" />
      {HOW.map(([title, text], index) => (
        <li key={title} className="relative flex gap-3 items-start">
          <span className="relative z-10 w-6 h-6 rounded-full bg-slate-900 text-white text-[10px] font-black flex items-center justify-center flex-shrink-0 ring-4 ring-white">{index + 1}</span>
          <div>
            <p className="text-[11px] font-black text-slate-900">{title}</p>
            <p className="text-[10px] font-semibold text-slate-500 mt-0.5">{text}</p>
          </div>
        </li>
      ))}
    </ol>
  </div>
);

export const GatherTool = () => {
  const [tab, setTab] = useState('income');
  return (
    <div className="space-y-4">
      <HowToGather />
      <div className="flex bg-slate-100 p-1 rounded-xl">
        {[['income', 'Income'], ['expense', 'Expenses']].map(([value, label]) => (
          <button
            key={value}
            onClick={() => setTab(value)}
            className={`flex-1 py-2.5 rounded-lg text-[10px] font-black uppercase tracking-widest ${tab === value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}
          >
            {label}
          </button>
        ))}
      </div>
      <QuickAddForm key={tab} kind={tab} placeholder={tab === 'income' ? 'e.g. Paycheck (net)' : 'e.g. Rent, Dining Out'} />
      {tab === 'expense' && (
        <p className="text-[10px] font-semibold text-slate-500">
          Required covers fixed living needs. Discretionary covers flexible choices.
        </p>
      )}
    </div>
  );
};

const LedgerRow = ({ item, tone, onRemove, onToggle }) => {
  const discretionary = tone === 'expense' && isDiscretionary(item);
  return (
    <div
      className={`flex items-center justify-between gap-2 p-2.5 pl-3 rounded-xl bg-white border border-slate-100 border-l-4 shadow-sm ${
        tone === 'income'
          ? 'border-l-emerald-400'
          : discretionary
          ? 'border-l-amber-400'
          : 'border-l-rose-500'
      }`}
    >
      <div className="min-w-0">
        <p className="text-[11px] font-black text-slate-900 truncate">{item.name}</p>
        <p className="text-[9px] font-bold text-slate-400">{fmt(item.amount)}/{FREQ_ABBR[item.frequency] || 'mo'}</p>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {onToggle && (
          <button
            onClick={onToggle}
            className={`px-2 py-1 rounded-md text-[8px] font-black uppercase transition-colors ${
              discretionary ? 'bg-amber-100 text-amber-700' : 'bg-rose-50 text-rose-600'
            }`}
          >
            {discretionary ? 'Discretionary' : 'Required'}
          </button>
        )}
        <button
          onClick={onRemove}
          aria-label={`Remove ${item.name}`}
          className="p-1.5 text-slate-300 hover:text-rose-500 transition-colors"
        >
          <SafeIcon icon={FiTrash2} className="text-xs" />
        </button>
      </div>
    </div>
  );
};

export const GatherVisual = () => {
  const { state, dispatch } = useFinancial();
  const { incomeStreams, expenses, goals } = state;
  const income = sumMonthly(incomeStreams);
  const required = sumMonthly(expenses.filter(item => !isDiscretionary(item)));
  const discretionary = sumMonthly(expenses.filter(isDiscretionary));
  const spend = required + discretionary;
  const net = income - spend;
  const requiredPercent = spend > 0 ? (required / spend) * 100 : 0;
  const years = Math.max(0, goals.targetAge - goals.currentAge);

  return (
    <div className="space-y-4">
      {/* Monthly Cash-Flow Snapshot */}
      <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-sm">
        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400 mb-2">Monthly cash-flow snapshot</p>
        
        {/* Responsive flex layout to prevent overlap on mobile */}
        <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
          <span className={`text-2xl sm:text-3xl font-black tracking-tighter leading-none ${net >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {fmt(net)}
          </span>
          <span className="text-[11px] font-bold text-slate-400">
            left before investing
          </span>
        </div>

        <div className="grid grid-cols-2 gap-3 mt-4 pt-4 border-t border-slate-100">
          <div>
            <p className="text-[8px] font-black uppercase text-slate-400">Income</p>
            <p className="text-sm font-black text-slate-900">{fmt(income)}</p>
          </div>
          <div>
            <p className="text-[8px] font-black uppercase text-slate-400">Expenses</p>
            <p className="text-sm font-black text-slate-900">{fmt(spend)}</p>
          </div>
        </div>
      </div>

      {/* Breakdown Bar */}
      <div className="p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
        <div className="h-2.5 rounded-full overflow-hidden flex bg-slate-100">
          <div className="bg-rose-500 h-full transition-all duration-300" style={{ width: `${requiredPercent}%` }} />
          <div className="bg-amber-400 h-full transition-all duration-300" style={{ width: `${spend > 0 ? 100 - requiredPercent : 0}%` }} />
        </div>
        <div className="flex justify-between gap-2 mt-3 text-[9px] font-black uppercase text-slate-600">
          <span className="text-rose-600">● Required {fmt(required)}</span>
          <span className="text-amber-600">● Flexible {fmt(discretionary)}</span>
        </div>
      </div>

      {/* Income in Budget */}
      <div className="space-y-2">
        <p className="text-[9px] font-black uppercase text-emerald-600 tracking-widest">Income in Budget</p>
        {incomeStreams.length === 0 && <p className="text-[10px] text-slate-400">No income yet.</p>}
        {incomeStreams.map(item => (
          <LedgerRow
            key={item.id}
            item={item}
            tone="income"
            onRemove={() => dispatch({ type: 'REMOVE_INCOME', id: item.id })}
          />
        ))}
      </div>

      {/* Expenses in Budget */}
      <div className="space-y-2">
        <p className="text-[9px] font-black uppercase text-rose-600 tracking-widest">Expenses in Budget</p>
        {expenses.length === 0 && <p className="text-[10px] text-slate-400">No expenses yet.</p>}
        {expenses.map(item => (
          <LedgerRow
            key={item.id}
            item={item}
            tone="expense"
            onRemove={() => dispatch({ type: 'REMOVE_EXPENSE', id: item.id })}
            onToggle={() =>
              dispatch({
                type: 'UPDATE_EXPENSE',
                payload: {
                  id: item.id,
                  accountType: isDiscretionary(item) ? 'bills' : 'discretionary'
                }
              })
            }
          />
        ))}
      </div>

      {discretionary > 0 && (
        <div className="p-4 bg-rose-50/50 border border-rose-100 rounded-2xl flex gap-3">
          <SafeIcon icon={FiTrendingUp} className="text-rose-600 flex-shrink-0" />
          <p className="text-[10px] font-semibold text-rose-950">
            If you invested {fmt(discretionary)}/mo for {years} years at {goals.expectedROI}%, it could grow to <strong>{fmt(futureValue(discretionary, goals.expectedROI, years))}</strong>. This is a comparison, not a recommendation to cut every flexible expense.
          </p>
        </div>
      )}
    </div>
  );
};